import { validatedDisplayView, extendDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `api-key` of the console gateway — over `apps/api/src/routes/api-key/` (design §27.9.2). Owned by plan-014
 * unit **C3**.
 *
 *   listWorkspaceApiKeys    `listWorkspaceApiKeys`    the keys of one workspace
 *   createWorkspaceApiKey   `createWorkspaceApiKey`   a workspace key — its value returned ONCE
 *   listAccountApiKeys      `listAccountApiKeys`      the organization's keys (billing, usage, documents)
 *   createAccountApiKey     `createAccountApiKey`     an organization key — its value returned ONCE
 *   rotateApiKey            `rotateApiKey`            a new value, the old key revoked — returned ONCE
 *   revokeApiKeyById        `revokeApiKey`            any key the person may manage, whichever workspace or the
 *                                                     organization it belongs to (the gateway's older `revokeApiKey`
 *                                                     calls the same operation; this one is the page's, so the
 *                                                     sandbox half revokes in the same store it lists)
 *
 * ── THE VALUE IS NEVER CACHED ────────────────────────────────────────────────────────────────────────────────
 * `createWorkspaceApiKey`, `createAccountApiKey` and `rotateApiKey` return the key's value once; the page holds it
 * in component state until its dialog closes. No query is ever made of it.
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in C3's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated
 * by the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through
 * {@link operationsOf}, a structural view of the client with exactly these methods (C1's, B2's and C2's precedent):
 * it compiles before and after the regeneration, and a client that still lacks one raises naming the regeneration.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readTextArray, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type ApiKeyScopeKindView = "workspace" | "account";
export type ApiKeyOwnerKindView = "person" | "account_integration";

/** One key as the Settings page shows it — never the key. */
export interface ApiKeyRecordView {
  readonly apiKeyId: string;
  readonly workspaceId: string | null;
  readonly scopeKind: ApiKeyScopeKindView;
  readonly ownerKind: ApiKeyOwnerKindView;
  /**
   * The person a person-owned key acts for (`null` for an integration key). A person-owned key is rotated by that
   * person alone (security review M-8: `403 permission_denied` for anyone else, who revokes it instead), so the page
   * offers Rotate only on the caller's own keys.
   */
  readonly ownerPersonId: string | null;
  readonly ownerName: string | null;
  readonly name: string;
  readonly keyPrefix: string;
  readonly scope: readonly string[];
  readonly rateLimitPerMinute: number;
  readonly rateClass: "standard" | "elevated";
  readonly allowedNetwork: readonly string[];
  /** ISO 8601, or `null` for a key issued before expiries existed. */
  readonly expiresAt: string | null;
  readonly expired: boolean;
  readonly lastUsedText: string | null;
  readonly createdText: string;
  readonly createdByName: string | null;
  readonly revoked: boolean;
  readonly revokedReason: string | null;
}

/** A key at the one moment its value exists. */
export interface IssuedApiKeyRecordView extends ApiKeyRecordView {
  /** Shown once, in a dialog that cannot be reopened. */
  readonly key: string;
}

/** What the create form sends. */
export interface ApiKeyRequestDraft {
  readonly name: string;
  readonly scope: readonly string[];
  /** ISO 8601, in the future. */
  readonly expiresAt: string;
  readonly allowedNetwork: readonly string[];
  readonly ownerKind: ApiKeyOwnerKindView;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The keys of one workspace, newest first, revoked ones included. */
    listWorkspaceApiKeys(workspaceId: string): Promise<readonly ApiKeyRecordView[]>;
    /** Issues a workspace key; the value comes back this once. */
    createWorkspaceApiKey(workspaceId: string, draft: ApiKeyRequestDraft): Promise<IssuedApiKeyRecordView>;
    /** The organization's keys (`billing:read`, `usage:read`, `document:read`). */
    listAccountApiKeys(accountId: string): Promise<readonly ApiKeyRecordView[]>;
    /** Issues an organization key; the value comes back this once. */
    createAccountApiKey(accountId: string, draft: ApiKeyRequestDraft): Promise<IssuedApiKeyRecordView>;
    /** Rotates a key: a new value (this once), the old key revoked. */
    rotateApiKey(apiKeyId: string, expiresAt: string): Promise<IssuedApiKeyRecordView>;
    /** Revokes a key — revoked, never deleted. */
    revokeApiKeyById(apiKeyId: string): Promise<void>;
  }
}

export type ApiKeyGatewaySlice = Pick<
  ConsoleGateway,
  | "listWorkspaceApiKeys"
  | "createWorkspaceApiKey"
  | "listAccountApiKeys"
  | "createAccountApiKey"
  | "rotateApiKey"
  | "revokeApiKeyById"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in C3's fragments. */
export const apiKeyGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

type Header = Readonly<Record<string, string>>;

interface ApiKeyOperations {
  listWorkspaceApiKeys(request: { path: { readonly workspace_id: string } }): Promise<ApiResult>;
  createWorkspaceApiKey(request: { path: { readonly workspace_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  listAccountApiKeys(request: { path: { readonly account_id: string } }): Promise<ApiResult>;
  createAccountApiKey(request: { path: { readonly account_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  rotateApiKey(request: { path: { readonly api_key_id: string }; body?: unknown; header?: Header }): Promise<ApiResult>;
  revokeApiKey(request: { path: { readonly api_key_id: string } }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): ApiKeyOperations {
  const held = client as unknown as Partial<Record<keyof ApiKeyOperations, unknown>>;
  return new Proxy({} as ApiKeyOperations, {
    get(_target, name: string) {
      const found = held[name as keyof ApiKeyOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_9a97e52c", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

function nullableText(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function oneOf<const T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const text = readText(value, path);
  if (!(allowed as readonly string[]).includes(text)) throw new Error(`${path} is ${text}, which this console does not know.`);
  return text as T;
}

/** One key off the wire (`ApiKeySummary`). */
export function apiKeyRecordOf(
  value: unknown,
  path: string,
  context: Pick<SliceContext, "formatWhen">,
  now: Date,
): ApiKeyRecordView {
  const one = readRecord(value, path);
  const lastUsed = nullableText(one["last_used_at"], `${path}.last_used_at`);
  const rateClass = oneOf(["standard", "elevated"] as const, one["rate_class"], `${path}.rate_class`);
  return validatedDisplayView({
    apiKeyId: readText(one["api_key_id"], `${path}.api_key_id`),
    workspaceId: nullableText(one["workspace_id"], `${path}.workspace_id`),
    scopeKind: oneOf(["workspace", "account"] as const, one["scope_kind"], `${path}.scope_kind`),
    ownerKind: oneOf(["person", "account_integration"] as const, one["owner_kind"], `${path}.owner_kind`),
    ownerPersonId: nullableText(one["owner_person_id"], `${path}.owner_person_id`),
    ownerName: nullableText(one["owner_name"], `${path}.owner_name`),
    name: readText(one["name"], `${path}.name`),
    keyPrefix: readText(one["key_prefix"], `${path}.key_prefix`),
    scope: readTextArray(one["scope"], `${path}.scope`),
    rateLimitPerMinute: readWholeNumber(one["rate_limit_per_minute"], `${path}.rate_limit_per_minute`),
    rateClass,
    allowedNetwork: readTextArray(one["allowed_network"], `${path}.allowed_network`),
    expiresAt: nullableText(one["expires_at"], `${path}.expires_at`),
    expired: readBoolean(one["expired"], `${path}.expired`),
    get lastUsedText() { return lastUsed === null ? null : context.formatWhen(lastUsed, now); },
    get createdText() { return context.formatWhen(readText(one["created_at"], `${path}.created_at`), now); },
    createdByName: nullableText(one["created_by_name"], `${path}.created_by_name`),
    revoked: one["revoked_at"] !== null && one["revoked_at"] !== undefined,
    revokedReason: nullableText(one["revoked_reason"], `${path}.revoked_reason`),
  });
}

function listOf(result: ApiResult, operationId: string, context: SliceContext): readonly ApiKeyRecordView[] {
  const now = context.now();
  const body = readRecord(assertOk(result), operationId);
  return readArray(body["data"], `${operationId}.data`).map((one, index) =>
    apiKeyRecordOf(one, `${operationId}.data[${index}]`, context, now),
  );
}

function issuedOf(result: ApiResult, operationId: string, context: SliceContext): IssuedApiKeyRecordView {
  const data = readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
  return extendDisplayView(apiKeyRecordOf(data, `${operationId}.data`, context, context.now()), { key: readText(data["key"], `${operationId}.data.key`) });
}

function bodyOf(draft: ApiKeyRequestDraft): Readonly<Record<string, unknown>> {
  return {
    name: draft.name,
    scope: draft.scope,
    expires_at: draft.expiresAt,
    allowed_network: draft.allowedNetwork,
    owner_kind: draft.ownerKind,
  };
}

/** A fresh `Idempotency-Key` per act: a retried request replays, a second click issues a second key. */
function idempotencyHeader(): Header {
  return { "idempotency-key": crypto.randomUUID() };
}

// ── the slice ─────────────────────────────────────────────────────────────────────────────────────────────

export function apiKeyApiSlice(client: WeTalkApiClient, context: SliceContext): ApiKeyGatewaySlice {
  const operation = operationsOf(client);
  return {
    async listWorkspaceApiKeys(workspaceId) {
      return listOf(await operation.listWorkspaceApiKeys({ path: { workspace_id: workspaceId } }), "listWorkspaceApiKeys", context);
    },
    async createWorkspaceApiKey(workspaceId, draft) {
      return issuedOf(
        await operation.createWorkspaceApiKey({ path: { workspace_id: workspaceId }, body: bodyOf(draft), header: idempotencyHeader() }),
        "createWorkspaceApiKey",
        context,
      );
    },
    async listAccountApiKeys(accountId) {
      return listOf(await operation.listAccountApiKeys({ path: { account_id: accountId } }), "listAccountApiKeys", context);
    },
    async createAccountApiKey(accountId, draft) {
      return issuedOf(
        await operation.createAccountApiKey({ path: { account_id: accountId }, body: bodyOf(draft), header: idempotencyHeader() }),
        "createAccountApiKey",
        context,
      );
    },
    async rotateApiKey(apiKeyId, expiresAt) {
      return issuedOf(
        await operation.rotateApiKey({ path: { api_key_id: apiKeyId }, body: { expires_at: expiresAt }, header: idempotencyHeader() }),
        "rotateApiKey",
        context,
      );
    },
    async revokeApiKeyById(apiKeyId) {
      assertOk(await operation.revokeApiKey({ path: { api_key_id: apiKeyId } }));
    },
  };
}
